import { useMemo } from 'react'

import {
    useInviteLinkSelection,
    useSearch,
    useSortedData,
} from '@/features/analytics/hooks'
import {
    AnalyticsTableRowTypeChannels,
    transformLinksToTableFormatChannels,
} from '@/features/analytics/types'
import {
    InviteLinkWithAnalyticsType,
    useUpdateInviteLinkMutation,
} from '@/shared/api'
import {
    useActiveUser,
    AdvertiserAccount,
    Channel,
} from '@/true-fsd/data/advertiser-account'
import { TableLoadingIndicator } from '@/shared/ui/loading-indicator/LoadingIndicator'

import { useEditSingleField } from '../shared/hooks'
import { ScrollableTable } from '../shared/ScrollableTable'
import '../inviteLinksTable.scss'

import { TableHeadRow } from './TableHeadRow'
import { TableRow } from './TableRow'
import { useMarkingStatus } from '@/shared/hooks'
// import { useCreativesByTypeAndId } from '@/entities/creative'

export type InviteLinksChannelsTableProps = {
    groupId: number
    activeChannel: Channel
    activeAccount: AdvertiserAccount | null
    inviteLinks: InviteLinkWithAnalyticsType[]
    isTableLoading: boolean
}

export const InviteLinksChannelsTable = ({
    groupId,
    activeChannel,
    activeAccount,
    inviteLinks,
    isTableLoading,
}: InviteLinksChannelsTableProps) => {
    const user = useActiveUser()
    const markingStatus = useMarkingStatus()

    const { selectedLinksSet, onLinkSelect } = useInviteLinkSelection({
        groupId,
    })

    const formattedRows = useMemo(() => {
        return transformLinksToTableFormatChannels(inviteLinks)
    }, [inviteLinks])

    const { mutate } = useUpdateInviteLinkMutation({
        channelId: activeChannel.channel_id,
    })

    const { filteredData, ...searchHook } =
        useSearch<AnalyticsTableRowTypeChannels>(formattedRows)
    const { sortedData, ...sortedDataHook } =
        useSortedData<AnalyticsTableRowTypeChannels>(filteredData)

    const editSingleFieldHook = useEditSingleField()

    return (
        <section className="invite-links-table">
            {isTableLoading && <TableLoadingIndicator />}
            <ScrollableTable
                type="channel"
                headRow={
                    <TableHeadRow
                        {...searchHook}
                        {...sortedDataHook}
                        activeChannel={activeChannel}
                        groupId={groupId}
                    />
                }
            >
                {sortedData.map((dataRow) => (
                    <TableRow
                        {...editSingleFieldHook}
                        {...{
                            groupId,
                            mutate,
                            user,
                            activeAccount,
                            activeChannel,
                            markingStatus,
                        }}
                        key={dataRow.link_id}
                        data={dataRow}
                        selected={selectedLinksSet.has(dataRow.link_id)}
                        onSelect={onLinkSelect}
                    />
                ))}
            </ScrollableTable>
        </section>
    )
}
